import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Badge, Flex, Menu, Text } from '@mantine/core';
import React, { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useTransactionDetailsController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/useTransactionDetailsController.ts';
import { useTransactionsController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/useTransactionsController.tsx';
import {
	GetTransactionRes,
	PermissionRole,
	TransactionWithSecurityTct,
} from '@dealstack/shared/lib/generated';
import { Main, MainContent } from './styled';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { ResourceType } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	AlertDescription,
	AlertTitle,
	Alert,
} from '@dealstack/frontend/shared/components/Alert/Alert.tsx';
import {
	CapMenu,
	StyledCapMenuItem,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CapMenu.tsx';
import { SplitTransactionDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/SplitTransactionDrawer/SplitTransactionDrawer.tsx';
import { SplitInterestDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/SplitInterestDrawer/SplitInterestDrawer.tsx';
import { isSplitInterestEligible } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility';
import { resolveTransactionFormSecurity } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/resolveTransactionFormSecurity.ts';
import { PairTransactionDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/PairTransactionDrawer/PairTransactionDrawer.tsx';
import { TransactionTabs } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/TransactionTabs/TransactionTabs.tsx';
import { TransactionOverview } from './TransactionOverview/TransactionOverview';
import { buildTransactionSentence } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/utils.ts';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { getTransactionErrorText } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionErrorText.ts';
import { TransactionProvenanceLine } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/Provenance/TransactionProvenanceLine';

interface Props {
	selectedEntityLoading?: boolean;
	personSelectOptions: {
		label: string;
		value: string;
	}[];
	mipSelectOptions: {
		label: string;
		value: string;
	}[];
}

export const TransactionDetails = ({
	selectedEntityLoading,
	personSelectOptions,
	mipSelectOptions,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const navigate = useNavigate();
	const { transactionId } = useParams();
	const { getTransactionDetailsRequest, refetchTransactionDetails, isTransactionLoading } =
		useTransactionDetailsController(transactionId);
	const [confirmDeleteOpened, setConfirmDeleteOpened] = useState<boolean>(false);
	const [transactionToDelete, setTransactionToDelete] = useState<TransactionWithSecurityTct>();
	const [isForceDelete, setIsForceDelete] = useState<boolean>(false);
	const { dealId, deal } = useContext(DealContext) || {};

	const [showSplitDrawer, setShowSplitDrawer] = useState<boolean>(false);
	const [showSplitInterestDrawer, setShowSplitInterestDrawer] = useState<boolean>(false);
	const [showPairDrawer, setShowPairDrawer] = useState<boolean>(false);

	const transaction = getTransactionDetailsRequest.data;

	const tsaConnected =
		(transaction?.sourcePgTsaId !== null || transaction?.destinationPgTsaId !== null) &&
		!isForceDelete;

	const isLoading = isTransactionLoading || selectedEntityLoading;

	const {
		onDelete,
		deleteTransactionRequest,
		userIsAdmin,
		securities,
		selectedEntityName,
		selectedEntityRegNumber,
		selectedEntityId,
	} = useTransactionsController();

	const breadcrumbs = useMemo(
		() =>
			DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.TRANSACTION_DETAILS]({
				tCapManage,
				dealId,
				personId: selectedEntityId,
				personDisplayName: selectedEntityName,
			}),
		[tCapManage, dealId, selectedEntityId, selectedEntityName]
	);

	useBreadcrumbs(breadcrumbs);

	const handleDeleteClick = async (transaction: TransactionWithSecurityTct) => {
		setTransactionToDelete(transaction);
		setIsForceDelete(false);

		setConfirmDeleteOpened(true);
	};

	const handleForceDeleteClick = async (transaction?: TransactionWithSecurityTct) => {
		if (!transaction) {
			showToastV2({
				state: 'error',
				message: tCapManage('toasts.errors.transaction_details_fail'),
			});
			return;
		}

		setTransactionToDelete(transaction);
		setIsForceDelete(true);
		setConfirmDeleteOpened(true);
	};

	const handleConfirmDelete = async () => {
		if (transactionToDelete?.id) {
			onDelete(transactionToDelete.id);
			deleteTransactionRequest.mutate(
				{ transactionId: transactionToDelete.id },
				{
					onSuccess: navigateToTransactionsList,
				}
			);
		}
	};

	const handleSplitClick = () => {
		setShowSplitDrawer(true);
	};

	const handlePairClick = () => {
		setShowPairDrawer(true);
	};

	// CAP-2011: "Update interest transferred" — a Both-titled TRANSFER/REDEMPTION/CANCELLATION on
	// a nominee-structure security can be split into its beneficial and legal legs.
	// CAP-1062: the feature flag keeps every nominee surface dark, including this action.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;
	// Resolved rather than looked up: the entity-scoped list misses the transaction's own security on a
	// holder's page, which hid this action there.
	const transactionSecurity = useMemo(
		() =>
			resolveTransactionFormSecurity({
				securityId: transaction?.securityId,
				securities,
				transactionSecurity: transaction?.security,
				dealHasNomineeStructure: deal?.hasNomineeStructure,
			}),
		[securities, transaction?.securityId, transaction?.security, deal?.hasNomineeStructure]
	);
	const showSplitInterest =
		nomineeStructuresEnabled &&
		isSplitInterestEligible(transaction?.type, transaction?.title, transactionSecurity);

	const navigateToTransactionsList = () => {
		navigate(`..${PEOPLE_ROUTES.TRANSACTIONS}`);
	};

	// CAP-1976: deleting one leg of an interest pair cascades to its linked leg — the confirm
	// copy must say so (the forbidden TSA variant keeps precedence).
	const isPairedLeg = !!transaction?.interestPairId;
	const getDeleteAlertTitle = () => {
		if (tsaConnected) {
			return tCapManage('delete.transaction.drawer.single.forbiddenTitle');
		}
		if (isPairedLeg) {
			return tCapManage('delete.transaction.drawer.single.pairedAlertTitle');
		}
		return tCapManage('delete.transaction.drawer.single.alertTitle');
	};
	const getDeleteAlertDescription = () => {
		if (tsaConnected) {
			return tCapManage('delete.transaction.drawer.single.forbiddenDescription');
		}
		if (isPairedLeg) {
			return tCapManage('delete.transaction.drawer.single.pairedAlertDescription');
		}
		return tCapManage('delete.transaction.drawer.single.alertDescription');
	};

	const getTransactionSentence = (transaction: TransactionWithSecurityTct) => {
		return buildTransactionSentence(transaction, securities, personSelectOptions);
	};

	const overviewContent = (
		<TransactionOverview
			transaction={transaction}
			transactionId={transactionId}
			onSplitClick={handleSplitClick}
			onPairClick={handlePairClick}
			onSplitInterestClick={() => setShowSplitInterestDrawer(true)}
			showSplitInterest={showSplitInterest}
			securities={securities}
			personSelectOptions={personSelectOptions}
			mipSelectOptions={mipSelectOptions}
			refetchTransactionDetails={refetchTransactionDetails}
			onTransactionReplaced={(newTransactionId) =>
				navigate(
					`..${PEOPLE_ROUTES.TRANSACTIONS}?globalSearch=${encodeURIComponent(newTransactionId)}`
				)
			}
			// Hands the blocked interest-pair dialog straight into the page's normal delete
			// confirmation — same typed confirmation, and its copy already says the linked leg
			// goes too.
			onDeletePair={() => transaction && handleDeleteClick(transaction)}
		/>
	);

	return (
		<PageAbsoluteContainer>
			<PageHeader
				pageTitle={
					transaction
						? getTransactionSentence(transaction)
						: tCapManage('transactions.details.title')
				}
			>
				<Flex
					justify={transaction?.deleted ? 'space-between' : 'flex-end'}
					align="center"
					gap="1rem"
					style={{ flex: 1 }}
				>
					{transaction?.deleted && (
						<Badge color="red" size="md" fw={400}>
							Deleted
						</Badge>
					)}
					<PermissionWrapper
						resourceType={ResourceType.DEAL}
						resourceId={defaultToEmptyString(dealId)}
						permissionRole={PermissionRole.EDITOR}
					>
						<CapMenu
							buttonTargetIcon={<Icon variant="more-v3" size="vlg" />}
							isDisabled={transaction?.deleted}
						>
							<Menu.Dropdown
								style={{
									boxShadow: '0px 16px 32px 0px #DCDFE0',
								}}
							>
								<Flex gap="0.25rem" direction="column" align="flex-start">
									<PermissionWrapper
										resourceType={ResourceType.DEAL}
										resourceId={defaultToEmptyString(dealId)}
										permissionRole={PermissionRole.ADMIN}
									>
										<StyledCapMenuItem
											p="0.5rem"
											onClick={() =>
												transaction && handleDeleteClick(transaction)
											}
											leftSection={<Icon variant="trash-can" size="md" />}
											color="red.6"
										>
											<Text size="B6Medium">
												{tCapManage('transactions.details.delete')}
											</Text>
										</StyledCapMenuItem>

										{userIsAdmin && (
											<StyledCapMenuItem
												p="0.5rem"
												onClick={() => handleForceDeleteClick(transaction)}
												leftSection={<Icon variant="trash-can" size="md" />}
												color="red.6"
											>
												<Text size="B6Medium">
													{tCapManage('transactions.details.force')}
												</Text>
											</StyledCapMenuItem>
										)}
									</PermissionWrapper>
								</Flex>
							</Menu.Dropdown>
						</CapMenu>
					</PermissionWrapper>
				</Flex>
			</PageHeader>
			{transaction && transaction.isTransactionValid === false && (
				<Alert state="danger" size="s">
					<AlertTitle>{tCapManage('invalid_transaction_alert.title')}</AlertTitle>
					<AlertDescription>
						{tCapManage('invalid_transaction_alert.description')}
					</AlertDescription>
					{/* Name the actual problem. The banner used to say only that the transaction was
					    invalid, which leaves the reader nowhere to go; the grid has always known it
					    was e.g. oversold. Same helper, so the two surfaces cannot drift. */}
					{getTransactionErrorText(transaction.transactionError, tCapManage) && (
						<AlertDescription>
							{getTransactionErrorText(transaction.transactionError, tCapManage)}
						</AlertDescription>
					)}
				</Alert>
			)}
			<TransactionProvenanceLine dealId={dealId ?? ''} transactionId={transactionId} />
			<Main>
				{isLoading && <InlineLoader />}
				<MainContent>
					<TransactionTabs
						transaction={transaction}
						personSelectOptions={personSelectOptions}
						overviewContent={overviewContent}
					/>
				</MainContent>
			</Main>
			<SplitTransactionDrawer
				opened={showSplitDrawer}
				onClose={() => {
					setShowSplitDrawer(false);
				}}
				transaction={(transaction as GetTransactionRes) || ({} as GetTransactionRes)}
				selectedEntityName={selectedEntityName}
				selectedEntityRegNumber={selectedEntityRegNumber}
				refreshTransactionsList={() => {
					if (transaction?.id) {
						navigate(
							`..${PEOPLE_ROUTES.TRANSACTIONS}?globalSearch=${encodeURIComponent(transaction.id)}`
						);
					} else {
						navigateToTransactionsList();
					}
				}}
			/>
			{showSplitInterest && (
				<SplitInterestDrawer
					opened={showSplitInterestDrawer}
					onClose={() => {
						setShowSplitInterestDrawer(false);
					}}
					transaction={(transaction as GetTransactionRes) || ({} as GetTransactionRes)}
					refreshTransactionsList={navigateToTransactionsList}
				/>
			)}
			<PairTransactionDrawer
				opened={showPairDrawer}
				onClose={() => {
					setShowPairDrawer(false);
				}}
				transaction={(transaction as GetTransactionRes) || ({} as GetTransactionRes)}
				securitiesNames={securities}
				selectedEntityName={selectedEntityName}
				selectedEntityRegNumber={selectedEntityRegNumber}
				refreshTransactionsList={(newTransactionId?: string) => {
					if (newTransactionId) {
						navigate(
							`..${PEOPLE_ROUTES.TRANSACTIONS}?globalSearch=${encodeURIComponent(newTransactionId)}`
						);
					} else {
						navigateToTransactionsList();
					}
				}}
				isFromDetailsPage={true}
			/>
			<ConfirmDeleteDrawer
				opened={confirmDeleteOpened}
				onClose={() => setConfirmDeleteOpened(false)}
				title={tCapManage('transaction.delete.drawer.title')}
				alertTitle={getDeleteAlertTitle()}
				alertDescription={getDeleteAlertDescription()}
				inputLabel={tCapManage('delete.transaction.drawer.single.inputLabel')}
				inputPlaceholder={tCapManage('delete.transaction.drawer.single.inputPlaceholder')}
				hideAlertIcon={true}
				confirmDeleteText={tCapManage('delete.transaction.drawer.single.confirmDeleteWord')}
				wordMismatchErrorMessage={tCapManage(
					'delete.transaction.drawer.single.wordMismatchError'
				)}
				deleteBtnText={tCapManage('delete.transaction.drawer.confirmDelete')}
				cancelBtnText={tCapManage('delete.transaction.drawer.cancel')}
				deleteSuccess={deleteTransactionRequest.isSuccess}
				deleteLoading={deleteTransactionRequest.isPending}
				handleDelete={handleConfirmDelete}
				tsaConnected={tsaConnected}
			/>
		</PageAbsoluteContainer>
	);
};
